import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { ClosedLostReasonTotal, ClosedLostReportTotals, ClosedLostRow, ClosedLostSortKey } from '../../types';
import { formatCurrency } from 'utils';

interface Props {
  rows: ClosedLostRow[];
  totals: ClosedLostReportTotals;
  reasonTotals: ClosedLostReasonTotal[];
  sortKey: ClosedLostSortKey;
  sortDir: 'asc' | 'desc';
  onSort: (key: ClosedLostSortKey) => void;
}

interface ColumnDef {
  key: ClosedLostSortKey;
  label: string;
  format: 'num' | 'dollar';
}

const STANDALONE_COLUMN: ColumnDef = { key: 'dealsLost', label: 'Deals Lost', format: 'num' };

const SUMMARY_COLUMNS: ColumnDef[] = [
  { key: 'dealValueLost', label: 'Deal Value Lost', format: 'dollar' },
  { key: 'grossLost', label: 'Gross Lost', format: 'dollar' },
  { key: 'avgDaysInPipeline', label: 'Avg Days in Pipeline', format: 'num' },
];

const DATA_COLUMNS: ColumnDef[] = [STANDALONE_COLUMN, ...SUMMARY_COLUMNS];

function rankBadgeSx(rank: number) {
  const colors: Record<number, { bg: string; color: string }> = {
    1: { bg: '#e8f5e9', color: '#2e7d32' },
    2: { bg: '#e3f2fd', color: '#1565c0' },
    3: { bg: '#fff8e1', color: '#f57f17' },
  };
  return colors[rank];
}

export const ClosedLostTable = ({ rows, totals, reasonTotals, sortKey, sortDir, onSort }: Props) => {
  const createSortHandler = (key: ClosedLostSortKey) => () => onSort(key);

  // Rank badges follow the active sort column, matching the pipeline & won performance table.
  const rankMap = useMemo(() => {
    const sorted = [...rows].sort((a, b) => (b[sortKey] as number) - (a[sortKey] as number));
    return sorted.slice(0, 3).reduce(
      (acc, r, i) => {
        acc[r.name] = i + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [rows, sortKey]);

  // Reason columns mirror the backend-computed reason totals (dealer-configured reasons vary by result set).
  const reasonColumns = reasonTotals;

  const getReasonCell = (row: ClosedLostRow, reasonLabel: string) => {
    const reason = row.lossReasons.find(r => r.reasonLabel === reasonLabel);
    const dealsLost = reason?.dealsLost ?? 0;
    const percentOfLost = reason?.percentOfLost ?? 0;
    return (
      // Fixed-width slots keep the deal count digits aligned across rows regardless of percentage width.
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box component="span" sx={{ minWidth: 20, textAlign: 'right', fontWeight: 600 }}>
          {dealsLost}
        </Box>
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 48, textAlign: 'left', pl: 0.5 }}
        >
          ({percentOfLost}%)
        </Typography>
      </Box>
    );
  };

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 480 }}>
        <Table stickyHeader size="small" sx={{ minWidth: 960 }}>
          <TableHead sx={{ '& .MuiTableCell-root': { backgroundColor: 'grey.100' } }}>
            <TableRow>
              <TableCell rowSpan={2} sx={{ minWidth: 160, zIndex: 4, verticalAlign: 'bottom' }}>
                Name
              </TableCell>
              <TableCell rowSpan={2} align="right" sx={{ whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                <TableSortLabel
                  active={sortKey === STANDALONE_COLUMN.key}
                  direction={sortKey === STANDALONE_COLUMN.key ? sortDir : 'desc'}
                  onClick={createSortHandler(STANDALONE_COLUMN.key)}
                >
                  {STANDALONE_COLUMN.label}
                </TableSortLabel>
              </TableCell>
              <TableCell colSpan={SUMMARY_COLUMNS.length} align="center">
                Summary
              </TableCell>
              {reasonColumns.length > 0 && (
                <TableCell colSpan={reasonColumns.length} align="center">
                  Loss Reason
                </TableCell>
              )}
            </TableRow>
            <TableRow>
              {SUMMARY_COLUMNS.map(col => (
                <TableCell key={col.key} align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={sortKey === col.key}
                    direction={sortKey === col.key ? sortDir : 'desc'}
                    onClick={createSortHandler(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              {reasonColumns.map(col => (
                <TableCell key={col.reasonLabel} align="right" sx={{ minWidth: 140, whiteSpace: 'nowrap' }}>
                  {col.reasonLabel}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map(row => {
              const rank = rankMap[row.name];
              return (
                <TableRow key={row.name} hover>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {rank ? (
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            fontWeight: 700,
                            flexShrink: 0,
                            bgcolor: rankBadgeSx(rank).bg,
                            color: rankBadgeSx(rank).color,
                          }}
                        >
                          {rank}
                        </Box>
                      ) : (
                        <Box sx={{ width: 20 }} />
                      )}
                      {row.name}
                    </Box>
                  </TableCell>
                  {DATA_COLUMNS.map(col => (
                    <TableCell key={col.key} align="right">
                      {col.format === 'dollar'
                        ? formatCurrency(row[col.key] as number, true)
                        : col.key === 'avgDaysInPipeline'
                          ? `${row[col.key]}d`
                          : row[col.key]}
                    </TableCell>
                  ))}
                  {reasonColumns.map(col => (
                    <TableCell key={col.reasonLabel} align="right">
                      {getReasonCell(row, col.reasonLabel)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>

          <TableFooter sx={{ '& .MuiTableCell-root': { backgroundColor: 'grey.100' } }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>
                Totals ({rows.length} {rows.length === 1 ? 'row' : 'rows'})
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {totals.totalLostDeals}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatCurrency(totals.totalValueLost, true)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatCurrency(totals.grossProfitLost, true)}
              </TableCell>
              <TableCell />
              {reasonColumns.map(col => (
                <TableCell key={col.reasonLabel} align="right">
                  {/* Matches the number/percent slot widths in the body rows so totals line up with the deal counts above. */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Box component="span" sx={{ minWidth: 20, textAlign: 'right', fontWeight: 700 }}>
                      {col.total}
                    </Box>
                    <Box component="span" sx={{ minWidth: 48, pl: 0.5 }} />
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        </Table>
      </Box>
    </TableContainer>
  );
};
